import { create } from 'zustand';
import { api, ApiError, errorText, setUnauthorizedHandler, uploadAttachment } from '../api/http';
import { connectSocket, disconnectSocket, getSocket, isVisible } from '../api/socket';
import type { Conversation, Me, Message, MessageStatus, Reaction, Receipt, ReplyRef } from '../api/types';
import { local, type OutboxItem } from '../db/local';
import { bindCallSocket } from '../calls/callManager';
import { clearChatNotifications, refreshPushSubscription, unsubscribePush } from '../lib/push';

export interface PendingInfo {
  status: 'sending' | 'failed';
  progress?: number;
  previewUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  voiceDurationMs?: number;
  error?: string;
}

/** A confirmed message, or a local one still in the outbox (`pending` set, seq = Infinity). */
export interface MessageItem extends Message {
  pending?: PendingInfo;
}

export type Connection = 'online' | 'connecting' | 'offline';

interface State {
  me: Me | null;
  authChecked: boolean;
  conversations: Record<string, Conversation>;
  messages: Record<string, Message[]>;
  pending: Record<string, MessageItem[]>;
  hasMore: Record<string, boolean>;
  loaded: Record<string, boolean>;
  connection: Connection;
  activeConversationId: string | null;
  visible: boolean;
  /** Message being replied to, per conversation (shown above the composer). */
  replyDraft: Record<string, ReplyRef>;
}

const initial = (): Omit<State, 'authChecked'> => ({
  me: null,
  conversations: {},
  messages: {},
  pending: {},
  hasMore: {},
  loaded: {},
  connection: navigator.onLine ? 'connecting' : 'offline',
  activeConversationId: null,
  visible: isVisible(),
  replyDraft: {},
});

export const useChat = create<State>(() => ({ ...initial(), authChecked: false }));
const get = useChat.getState;
const set = useChat.setState;

const PAGE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

export function messageStatus(m: MessageItem, conv: Conversation | undefined): MessageStatus {
  if (m.pending) return m.pending.status;
  if (!conv) return 'sent';
  if (m.seq <= conv.peerReadSeq) return 'read';
  if (m.seq <= conv.peerDeliveredSeq) return 'delivered';
  return 'sent';
}

export const sortedConversations = (s: State) =>
  Object.values(s.conversations).sort(
    (a, b) => Date.parse(b.lastMessageAt ?? b.createdAt) - Date.parse(a.lastMessageAt ?? a.createdAt),
  );

function mergeSorted(existing: Message[] | undefined, incoming: Message[]): Message[] {
  const bySeq = new Map((existing ?? []).map((m) => [m.seq, m]));
  for (const m of incoming) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

function storeMessages(conversationId: string, incoming: Message[]) {
  if (!incoming.length) return;
  const clientIds = new Set(incoming.map((m) => m.clientId));
  set((s) => ({
    messages: { ...s.messages, [conversationId]: mergeSorted(s.messages[conversationId], incoming) },
    pending: { ...s.pending, [conversationId]: (s.pending[conversationId] ?? []).filter((p) => !clientIds.has(p.clientId)) },
  }));
  local.putMessages(incoming).then(() => local.trimMessages(conversationId));
}

function patchConversation(id: string, patch: Partial<Conversation>) {
  set((s) => (s.conversations[id] ? { conversations: { ...s.conversations, [id]: { ...s.conversations[id], ...patch } } } : {}));
}

function upsertConversations(list: Conversation[], replace = false) {
  set((s) => {
    const next = replace ? {} : { ...s.conversations };
    for (const c of list) next[c.id] = c;
    return { conversations: next };
  });
  local.setConversations(Object.values(get().conversations));
  updateBadge();
}

function updateBadge() {
  const total = Object.values(get().conversations).reduce((n, c) => n + c.unreadCount, 0);
  document.title = total ? `(${total}) Evo` : 'Evo';
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (total) nav.setAppBadge?.(total).catch(() => {});
  else nav.clearAppBadge?.().catch(() => {});
}

// ── Auth / lifecycle ──────────────────────────────────────────────────────────

export async function bootstrap() {
  setUnauthorizedHandler(() => void signedOut());
  const cached = await local.getMe();
  if (cached) {
    // Signed in before: open the app from the device cache right away instead of waiting for the network
    // (on a phone the first request can take seconds). The session is confirmed in the background.
    await signedIn(cached);
    set({ authChecked: true });
    void confirmSession(cached);
    return;
  }
  try {
    const { user } = await api<{ user: Me }>('GET', '/api/auth/me');
    await signedIn(user);
  } catch {
    set({ me: null });
  } finally {
    set({ authChecked: true });
  }
}

async function confirmSession(cached: Me) {
  try {
    const { user } = await api<{ user: Me }>('GET', '/api/auth/me');
    if (user.id !== cached.id) {
      // Another account's session (signed in elsewhere in this browser): drop this one's data.
      await signedOut();
      await signedIn(user);
    } else {
      await signedIn(user); // fresh profile
    }
  } catch (err) {
    // Offline or server trouble: keep the cached session, the socket reconnects when the network returns.
    if (err instanceof ApiError && !err.retryable && get().me?.id === cached.id) await signedOut();
  }
}

export async function signedIn(user: Me) {
  const alreadyRunning = get().me?.id === user.id && getSocket();
  set({ me: user });
  local.setMe(user);
  if (alreadyRunning) return;
  const cachedConversations = await local.getConversations();
  if (cachedConversations.length && !Object.keys(get().conversations).length) upsertConversations(cachedConversations, true);
  await restoreOutbox();
  startRealtime();
  void refreshPushSubscription();
}

export async function signedOut() {
  disconnectSocket();
  outbox.clear();
  // Avatars and photo previews cached by the service worker belong to this account.
  await caches?.delete('evo-media').catch(() => {});
  await local.clear();
  set({ ...initial(), authChecked: true });
}

export async function logout() {
  await unsubscribePush().catch(() => {});
  await api('POST', '/api/auth/logout', {}).catch(() => {});
  await signedOut();
}

export function setMe(user: Me) {
  set({ me: user });
  local.setMe(user);
}

// ── Realtime ──────────────────────────────────────────────────────────────────

let listenersBound = false;

function startRealtime() {
  const socket = connectSocket();
  bindCallSocket(socket);
  set({ connection: navigator.onLine ? 'connecting' : 'offline' });

  socket.on('connect', () => {
    set({ connection: 'online' });
    socket.emit('presence', { visible: isVisible() });
    void sync();
    void processOutbox(true);
  });
  socket.on('disconnect', () => set({ connection: navigator.onLine ? 'connecting' : 'offline' }));
  socket.on('connect_error', async (err) => {
    set({ connection: navigator.onLine ? 'connecting' : 'offline' });
    if (err.message === 'unauthorized') {
      // Session gone (logged out elsewhere, password reset): confirm and sign out.
      await api('GET', '/api/auth/me').catch(async (e) => {
        if (e instanceof ApiError && e.status === 401) await signedOut();
      });
    }
  });
  socket.on('message:new', (m: Message) => void onMessageNew(m));
  socket.on('receipt', (r: Receipt) => onReceipt(r));
  socket.on('message:reactions', (e: ReactionsEvent) => onReactions(e));

  if (listenersBound) return;
  listenersBound = true;
  // Heartbeat: the server only trusts a recent "visible" (see hasVisibleSocket).
  setInterval(() => {
    if (isVisible()) getSocket()?.emit('presence', { visible: true });
  }, 10_000);
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    const visible = isVisible();
    set({ visible });
    if (!visible) {
      hiddenAt = Date.now();
      getSocket()?.emit('presence', { visible: false });
      return;
    }
    resume(Date.now() - hiddenAt);
  });
  // Restored from the back/forward cache (also after the OS froze the app).
  window.addEventListener('pageshow', (e) => e.persisted && resume(Number.POSITIVE_INFINITY));
  window.addEventListener('online', () => {
    if (get().connection === 'offline') set({ connection: 'connecting' });
    const s = getSocket();
    if (s && !s.connected) s.connect();
    void processOutbox(true);
  });
  window.addEventListener('offline', () => set({ connection: 'offline' }));
}

// Away longer than this, the socket may be a "zombie": the OS suspended the app (locked screen), the
// connection died, but the page has not noticed yet (it would only after the ~40 s ping timeout).
const RESUME_RECONNECT_MS = 15_000;

/** Back in the foreground: make sure the connection is alive and catch up on what was missed. */
function resume(awayMs: number) {
  const s = getSocket();
  if (!s) return;
  if (!s.connected) {
    s.connect(); // the "connect" handler syncs
  } else if (awayMs > RESUME_RECONNECT_MS) {
    set({ connection: 'connecting' });
    s.disconnect();
    s.connect(); // fresh connection; the "connect" handler syncs and resends the outbox
  } else {
    s.emit('presence', { visible: true });
    void sync();
  }
  const id = get().activeConversationId;
  if (id) void markRead(id);
}

/** Catch-up after (re)connect: conversation list, gaps in open histories, delivery acks. */
export async function sync() {
  try {
    const { conversations } = await api<{ conversations: Conversation[] }>('GET', '/api/conversations');
    upsertConversations(conversations, true);
    for (const c of conversations) {
      if (c.lastSeq > c.myDeliveredSeq && c.lastMessage?.senderId !== get().me?.id) ackDelivered(c.id, c.lastSeq);
    }
    for (const id of Object.keys(get().loaded)) {
      await fetchAfter(id);
      await refreshLatest(id);
    }
    const active = get().activeConversationId;
    if (active) void markRead(active);
  } catch {
    /* retried on next reconnect */
  }
}

async function onMessageNew(m: Message) {
  const s = get();
  let conv = s.conversations[m.conversationId];
  if (!conv) {
    try {
      conv = (await api<{ conversation: Conversation }>('GET', `/api/conversations/${m.conversationId}`)).conversation;
      upsertConversations([conv]);
    } catch {
      return;
    }
  }
  const fromMe = m.senderId === s.me?.id;
  const isActive = s.activeConversationId === m.conversationId && s.visible;
  if (m.seq > conv.lastSeq) {
    patchConversation(m.conversationId, {
      lastSeq: m.seq,
      lastMessage: m,
      lastMessageAt: m.createdAt,
      unreadCount: fromMe ? 0 : isActive ? conv.unreadCount : conv.unreadCount + 1,
      ...(fromMe ? { myReadSeq: m.seq } : {}),
    });
    local.setConversations(Object.values(get().conversations));
    updateBadge();
  }
  if (s.loaded[m.conversationId]) {
    const list = s.messages[m.conversationId] ?? [];
    const last = list.length ? list[list.length - 1].seq : 0;
    if (m.seq > last + 1) await fetchAfter(m.conversationId);
    else storeMessages(m.conversationId, [m]);
  }
  if (!fromMe) {
    if (isActive) void markRead(m.conversationId);
    else ackDelivered(m.conversationId, m.seq);
  }
}

function onReceipt(r: Receipt) {
  const conv = get().conversations[r.conversationId];
  if (!conv) return;
  if (r.userId === get().me?.id) {
    // Another device of mine read the chat.
    patchConversation(r.conversationId, {
      myReadSeq: r.readSeq,
      myDeliveredSeq: r.deliveredSeq,
      ...(r.readSeq >= conv.lastSeq ? { unreadCount: 0 } : {}),
    });
    updateBadge();
  } else {
    patchConversation(r.conversationId, {
      peerDeliveredSeq: Math.max(conv.peerDeliveredSeq, r.deliveredSeq),
      peerReadSeq: Math.max(conv.peerReadSeq, r.readSeq),
    });
  }
}

// ── Reactions ─────────────────────────────────────────────────────────────────

interface ReactionsEvent {
  conversationId: string;
  messageId: string;
  reactions: Reaction[];
}

/** Sets a message's reactions (in the open history and the chat-list preview) and caches it. */
export function applyReactions(conversationId: string, messageId: string, reactions: Reaction[]) {
  let updated: Message | undefined;
  let lastPatched = false;
  set((s) => {
    const patch: Partial<State> = {};
    const list = s.messages[conversationId];
    const i = list?.findIndex((m) => m.id === messageId) ?? -1;
    if (list && i >= 0) {
      const next = list.slice();
      next[i] = updated = { ...list[i], reactions };
      patch.messages = { ...s.messages, [conversationId]: next };
    }
    const conv = s.conversations[conversationId];
    if (conv?.lastMessage?.id === messageId) {
      lastPatched = true;
      patch.conversations = { ...s.conversations, [conversationId]: { ...conv, lastMessage: { ...conv.lastMessage, reactions } } };
    }
    return patch;
  });
  if (updated) void local.putMessages([updated]);
  if (lastPatched) void local.setConversations(Object.values(get().conversations));
}

// Per message: requests are sent one at a time (toggles do not commute), and while any is in flight the
// optimistic state stays on screen; the last known server state is applied once the burst is over.
const reactionSync = new Map<string, { pending: number; confirmed: Reaction[]; chain: Promise<void> }>();

function onReactions(e: ReactionsEvent) {
  const sync = reactionSync.get(e.messageId);
  if (sync) sync.confirmed = e.reactions;
  else applyReactions(e.conversationId, e.messageId, e.reactions);
}

export async function toggleReaction(conversationId: string, messageId: string, emoji: string) {
  const me = get().me;
  const msg = get().messages[conversationId]?.find((m) => m.id === messageId);
  if (!me || !msg) return;
  const current = msg.reactions ?? [];
  const mine = current.find((r) => r.userId === me.id);
  const next = current.filter((r) => r.userId !== me.id);
  if (mine?.emoji !== emoji) next.push({ emoji, userId: me.id, createdAt: new Date().toISOString() });
  applyReactions(conversationId, messageId, next);

  let sync = reactionSync.get(messageId);
  if (!sync) reactionSync.set(messageId, (sync = { pending: 0, confirmed: current, chain: Promise.resolve() }));
  const entry = sync;
  entry.pending++;
  entry.chain = entry.chain.then(async () => {
    try {
      const res = await api<ReactionsEvent>('POST', `/api/conversations/${conversationId}/messages/${messageId}/reactions`, { emoji });
      entry.confirmed = res.reactions;
    } catch (err) {
      console.warn('reaction failed', err);
    }
    if (--entry.pending === 0) {
      reactionSync.delete(messageId);
      applyReactions(conversationId, messageId, entry.confirmed);
    }
  });
  await entry.chain;
}

// ── Receipts ──────────────────────────────────────────────────────────────────

const deliveredQueue = new Map<string, number>();
let deliveredTimer: ReturnType<typeof setTimeout> | undefined;

function ackDelivered(conversationId: string, seq: number) {
  deliveredQueue.set(conversationId, Math.max(seq, deliveredQueue.get(conversationId) ?? 0));
  clearTimeout(deliveredTimer);
  deliveredTimer = setTimeout(() => {
    const batch = [...deliveredQueue];
    deliveredQueue.clear();
    for (const [id, deliveredSeq] of batch) {
      api('POST', `/api/conversations/${id}/receipts`, { deliveredSeq }).catch(() => {});
    }
  }, 300);
}

const readInFlight = new Map<string, number>();

export async function markRead(conversationId: string) {
  if (!get().visible) return;
  void clearChatNotifications(conversationId);
  const conv = get().conversations[conversationId];
  if (!conv) return;
  const seq = conv.lastSeq;
  if (seq <= conv.myReadSeq && conv.unreadCount === 0) return;
  if ((readInFlight.get(conversationId) ?? 0) >= seq) return;
  readInFlight.set(conversationId, seq);
  patchConversation(conversationId, { unreadCount: 0, myReadSeq: seq });
  updateBadge();
  try {
    await api('POST', `/api/conversations/${conversationId}/receipts`, { readSeq: seq });
  } catch {
    readInFlight.delete(conversationId);
  }
}

// ── History ───────────────────────────────────────────────────────────────────

export async function openConversation(id: string) {
  set({ activeConversationId: id });
  if (!get().conversations[id]) {
    try {
      const { conversation } = await api<{ conversation: Conversation }>('GET', `/api/conversations/${id}`);
      upsertConversations([conversation]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw err;
    }
  }
  if (!get().loaded[id]) {
    const cached = await local.getMessages(id);
    if (cached.length && !get().messages[id]?.length) {
      set((s) => ({ messages: { ...s.messages, [id]: cached } }));
    }
    try {
      const { messages } = await api<{ messages: Message[] }>('GET', `/api/conversations/${id}/messages?limit=${PAGE}`);
      // Replace (not merge) so stale cached messages (e.g. deleted by retention) disappear.
      set((s) => ({
        messages: { ...s.messages, [id]: mergeSorted([], messages) },
        hasMore: { ...s.hasMore, [id]: messages.length === PAGE },
        loaded: { ...s.loaded, [id]: true },
      }));
      local.putMessages(messages);
    } catch {
      set((s) => ({ hasMore: { ...s.hasMore, [id]: false } }));
    }
  }
  void markRead(id);
}

export function closeConversation(id: string) {
  if (get().activeConversationId === id) set({ activeConversationId: null });
}

export async function loadOlder(id: string) {
  const list = get().messages[id];
  if (!list?.length || !get().hasMore[id]) return;
  const { messages } = await api<{ messages: Message[] }>('GET', `/api/conversations/${id}/messages?before=${list[0].seq}&limit=${PAGE}`);
  set((s) => ({
    messages: { ...s.messages, [id]: mergeSorted(s.messages[id], messages) },
    hasMore: { ...s.hasMore, [id]: messages.length === PAGE },
  }));
}

/** Re-reads the latest page so reactions changed while offline replace the cached ones. */
async function refreshLatest(id: string) {
  try {
    const { messages } = await api<{ messages: Message[] }>('GET', `/api/conversations/${id}/messages?limit=${PAGE}`);
    storeMessages(id, messages);
  } catch {
    /* retried on next reconnect */
  }
}

async function fetchAfter(id: string) {
  for (let i = 0; i < 20; i++) {
    const list = get().messages[id] ?? [];
    const after = list.length ? list[list.length - 1].seq : 0;
    const { messages } = await api<{ messages: Message[] }>('GET', `/api/conversations/${id}/messages?after=${after}&limit=100`);
    storeMessages(id, messages);
    if (messages.length < 100) return;
  }
}

export async function startConversation(userId: string): Promise<Conversation> {
  const { conversation } = await api<{ conversation: Conversation }>('POST', '/api/conversations', { userId });
  upsertConversations([conversation]);
  return conversation;
}

// ── Sending (outbox) ──────────────────────────────────────────────────────────

const previewUrls = new Map<string, string>();

/** What a reply quote shows of a message. */
export function replyRefOf(m: Message): ReplyRef {
  return {
    id: m.id,
    senderId: m.senderId,
    kind: m.kind,
    text: typeof m.payload?.text === 'string' ? m.payload.text.slice(0, 200) : null,
    attachmentKind: m.attachments[0]?.kind ?? null,
  };
}

export function setReplyDraft(conversationId: string, m: Message | null) {
  set((s) => {
    const replyDraft = { ...s.replyDraft };
    if (m) replyDraft[conversationId] = replyRefOf(m);
    else delete replyDraft[conversationId];
    return { replyDraft };
  });
}

/** The reply being composed in this conversation, consumed by the next message sent. */
function takeReply(conversationId: string): ReplyRef | undefined {
  const r = get().replyDraft[conversationId];
  if (r) setReplyDraft(conversationId, null);
  return r;
}

function toPendingItem(o: OutboxItem, me: Me): MessageItem {
  if (o.forward) {
    // Shown as the original (its attachments are readable by this user) until the copy is confirmed.
    return {
      ...o.forward,
      id: `local:${o.clientId}`,
      conversationId: o.conversationId,
      seq: Number.POSITIVE_INFINITY,
      senderId: me.id,
      clientId: o.clientId,
      reactions: [],
      replyTo: null,
      forwardedFrom: o.forward.forwardedFrom,
      createdAt: o.createdAt,
      pending: { status: o.failed ? 'failed' : 'sending', error: o.error },
    };
  }
  let previewUrl: string | undefined;
  if (o.file && (/^(image|video|audio)\//.test(o.file.type) || o.voiceDurationMs != null)) {
    previewUrl = previewUrls.get(o.clientId) ?? URL.createObjectURL(o.file);
    previewUrls.set(o.clientId, previewUrl);
  }
  return {
    id: `local:${o.clientId}`,
    conversationId: o.conversationId,
    seq: Number.POSITIVE_INFINITY,
    senderId: me.id,
    clientId: o.clientId,
    kind: o.kind,
    envelopeVersion: 1,
    encryption: 'none',
    payload: o.text ? { text: o.text } : {},
    attachments: [],
    reactions: [],
    replyTo: o.replyTo ?? null,
    createdAt: o.createdAt,
    pending: {
      status: o.failed ? 'failed' : 'sending',
      previewUrl,
      fileName: o.fileName,
      fileSize: o.file?.size,
      fileType: o.file?.type,
      voiceDurationMs: o.voiceDurationMs,
      error: o.error,
    },
  };
}

function setPending(clientId: string, conversationId: string, patch: Partial<PendingInfo> | null) {
  set((s) => {
    const list = s.pending[conversationId] ?? [];
    return {
      pending: {
        ...s.pending,
        [conversationId]:
          patch === null
            ? list.filter((p) => p.clientId !== clientId)
            : list.map((p) => (p.clientId === clientId ? { ...p, pending: { ...p.pending!, ...patch } } : p)),
      },
    };
  });
}

// The outbox lives in memory (the original File objects are what gets uploaded); IndexedDB is a
// best-effort copy so unsent messages survive a reload.
const outbox = new Map<string, OutboxItem>();
const outboxItems = () => [...outbox.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
function saveOutboxItem(o: OutboxItem) {
  outbox.set(o.clientId, o);
  return local.outbox.put(o);
}
function dropOutboxItem(clientId: string) {
  outbox.delete(clientId);
  return local.outbox.delete(clientId);
}

async function enqueue(o: OutboxItem) {
  const me = get().me!;
  outbox.set(o.clientId, o);
  void local.outbox.put(o);
  set((s) => ({
    pending: { ...s.pending, [o.conversationId]: [...(s.pending[o.conversationId] ?? []), toPendingItem(o, me)] },
  }));
}

async function restoreOutbox() {
  const me = get().me;
  if (!me) return;
  const items = await local.outbox.all();
  for (const o of items) if (!outbox.has(o.clientId)) outbox.set(o.clientId, o);
  const pending: Record<string, MessageItem[]> = {};
  for (const o of items) (pending[o.conversationId] ??= []).push(toPendingItem(o, me));
  set({ pending });
}

export async function sendText(conversationId: string, text: string) {
  const trimmed = text.trim();
  if (!trimmed) return;
  await enqueue({
    clientId: crypto.randomUUID(),
    conversationId,
    kind: 'text',
    text: trimmed,
    replyTo: takeReply(conversationId),
    createdAt: new Date().toISOString(),
  });
  void processOutbox();
}

/** Sends a copy of `m` to another conversation (the server copies its files). `authorName`: who wrote `m`. */
export async function forwardMessage(conversationId: string, m: Message, authorName: string) {
  if (m.kind !== 'text' && m.kind !== 'media') return;
  await enqueue({
    clientId: crypto.randomUUID(),
    conversationId,
    kind: m.kind,
    forward: { ...m, reactions: [], forwardedFrom: m.forwardedFrom ?? { userId: m.senderId, name: authorName } },
    createdAt: new Date().toISOString(),
  });
  void processOutbox();
}

/** Each file becomes its own message, sent sequentially (TЗ 3.4). */
export async function sendFiles(conversationId: string, files: File[], caption?: string) {
  const replyTo = takeReply(conversationId);
  for (const [i, file] of files.entries()) {
    await enqueue({
      replyTo: i === 0 ? replyTo : undefined,
      clientId: crypto.randomUUID(),
      conversationId,
      kind: 'media',
      file,
      fileName: file.name,
      text: i === 0 ? caption?.trim() || undefined : undefined,
      createdAt: new Date().toISOString(),
    });
  }
  void processOutbox();
}

export async function sendVoice(conversationId: string, blob: Blob, durationMs: number, extension: string) {
  await enqueue({
    clientId: crypto.randomUUID(),
    conversationId,
    kind: 'media',
    file: blob,
    fileName: `voice-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`,
    voiceDurationMs: durationMs,
    replyTo: takeReply(conversationId),
    createdAt: new Date().toISOString(),
  });
  void processOutbox();
}

let processing = false;
let rerun = false;

/**
 * Sends queued messages in order. Retries are safe: the server deduplicates by clientId,
 * and an upload is not repeated once its attachment id is known.
 */
export async function processOutbox(retryFailed = false) {
  if (processing) {
    rerun = true;
    return;
  }
  processing = true;
  try {
    for (const item of outboxItems()) {
      if (item.failed && !(retryFailed && item.retryable)) continue;
      if (!getSocket()?.connected && !navigator.onLine) break;
      setPending(item.clientId, item.conversationId, { status: 'sending', error: undefined });
      try {
        if (item.kind === 'media' && !item.attachmentId && !item.forward) {
          if (!item.file) throw new ApiError(400, 'file_lost');
          const { attachment } = await uploadAttachment(
            item.conversationId,
            item.file,
            item.fileName ?? 'file',
            (progress) => setPending(item.clientId, item.conversationId, { progress }),
            item.voiceDurationMs != null ? { durationMs: item.voiceDurationMs } : undefined,
          );
          item.attachmentId = attachment.id;
          await saveOutboxItem(item);
        }
        const { message } = item.forward
          ? await api<{ message: Message }>('POST', `/api/conversations/${item.conversationId}/forward`, {
              clientId: item.clientId,
              messageId: item.forward.id,
            })
          : await api<{ message: Message }>('POST', `/api/conversations/${item.conversationId}/messages`, {
              clientId: item.clientId,
              kind: item.kind,
              envelopeVersion: 1,
              encryption: 'none',
              payload: item.text ? { text: item.text } : {},
              attachmentIds: item.attachmentId ? [item.attachmentId] : [],
              replyToId: item.replyTo?.id,
            });
        await dropOutboxItem(item.clientId);
        const url = previewUrls.get(item.clientId);
        if (url) setTimeout(() => URL.revokeObjectURL(url), 10000);
        previewUrls.delete(item.clientId);
        if (get().loaded[item.conversationId]) storeMessages(item.conversationId, [message]);
        else setPending(item.clientId, item.conversationId, null);
      } catch (err) {
        const transient = err instanceof ApiError && err.retryable;
        const error = transient ? 'Не отправлено — повторим при подключении' : errorText(err);
        item.failed = true;
        item.retryable = transient;
        item.error = error;
        await saveOutboxItem(item);
        setPending(item.clientId, item.conversationId, { status: 'failed', error, progress: undefined });
        // Network-level failure: stop here to preserve order; resumed on reconnect.
        if (transient) break;
      }
    }
  } finally {
    processing = false;
    if (rerun) {
      rerun = false;
      void processOutbox(retryFailed);
    }
  }
}

export async function retryMessage(clientId: string) {
  const item = outbox.get(clientId);
  if (!item) return;
  item.failed = false;
  await saveOutboxItem(item);
  setPending(clientId, item.conversationId, { status: 'sending', error: undefined });
  void processOutbox();
}

export async function discardMessage(conversationId: string, clientId: string) {
  await dropOutboxItem(clientId);
  const url = previewUrls.get(clientId);
  if (url) URL.revokeObjectURL(url);
  previewUrls.delete(clientId);
  setPending(clientId, conversationId, null);
}
